<script lang="ts">
export default {
  name: 'PolicyTypeEditModal',
};
</script>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import Combobox from '@/components/v2/Combobox.vue';
import { AlertCircle } from '@lucide/vue';

interface PolicyType {
  policyType: string;
  policyDescription: string;
  coverageType: string;
  casualtyFlag: boolean;
}

const props = defineProps<{
  show: boolean;
  policyType: PolicyType | null;
  coverageTypes: string[];
}>();

const emit = defineEmits<{
  cancelModal: [];
  saveDescription: [policyType: PolicyType];
  saveCoverageType: [policyType: PolicyType, coverageType: string];
}>();

// Reactive state
const showModal = ref(false);
const loading = ref(false);
const error = ref('');

// Form fields
const description = ref(props.policyType?.policyDescription || '');
const selectedCoverageType = ref(props.policyType?.coverageType || '');
const newCoverageTypeName = ref('');
const isCreatingNew = ref(false);

// Computed
const coverageTypeOptions = computed(() => {
  if (!props.coverageTypes || !Array.isArray(props.coverageTypes)) {
    return [];
  }
  return props.coverageTypes.map((type) => ({
    value: type,
    label: type,
  }));
});

// Watch for prop changes to update form values
watch(
  () => props.policyType,
  (newPolicyType) => {
    if (!showModal.value && newPolicyType) {
      description.value = newPolicyType.policyDescription;
      selectedCoverageType.value = newPolicyType.coverageType;
    }
  },
);

watch(
  () => props.show,
  (newShow) => {
    if(newShow) {
      openModal();
    }
  },
);

// Methods
const openModal = () => {
  if (!props.policyType){
    console.error('No policy type provided to edit.');
    return;
  }

  description.value = props.policyType.policyDescription;
  selectedCoverageType.value = props.policyType.coverageType;
  newCoverageTypeName.value = '';
  isCreatingNew.value = false;
  error.value = '';
  showModal.value = true;
};

const handleCoverageTypeSelect = (option: { value: string; label: string }) => {
  selectedCoverageType.value = option.value;
};

const handleSave = async () => {
  if (!props.policyType) {
    error.value = 'No policy type to save';
    return;
  }

  try {
    loading.value = true;
    error.value = '';

    // Validate inputs
    if (!description.value.trim()) {
      error.value = 'Policy description cannot be empty';
      loading.value = false;
      return;
    }

    let coverageTypeToSave = '';

    if (isCreatingNew.value) {
      // Validate new coverage type name
      if (!newCoverageTypeName.value.trim()) {
        error.value = 'Coverage type name cannot be empty';
        loading.value = false;
        return;
      }

      if (props.coverageTypes.includes(newCoverageTypeName.value.trim())) {
        error.value = 'Coverage type already exists';
        loading.value = false;
        return;
      }

      coverageTypeToSave = newCoverageTypeName.value.trim();
    } else {
      if (!selectedCoverageType.value) {
        error.value = 'Please select a coverage type';
        loading.value = false;
        return;
      }
      coverageTypeToSave = selectedCoverageType.value;
    }

    // Create updated policy type object
    const updatedPolicyType = {
      ...props.policyType,
      policyDescription: description.value.trim(),
    };

    // Emit description change if it changed
    if (description.value.trim() !== props.policyType.policyDescription) {
      emit('saveDescription', updatedPolicyType);
    }

    // Emit coverage type change if it changed
    if (coverageTypeToSave !== props.policyType.coverageType) {
      emit('saveCoverageType', props.policyType, coverageTypeToSave);
    }

    showModal.value = false;
    loading.value = false;
  } catch (e) {
    console.error(e);
    loading.value = false;
  }
};

const handleCancel = () => {
  description.value = "";
  selectedCoverageType.value = "";
  newCoverageTypeName.value = '';
  isCreatingNew.value = false;
  error.value = '';
  showModal.value = false;
  emit('cancelModal');
};
</script>

<template>
  <div>
    <AppModal
      :show="showModal"
      :title="`Edit Policy Type: ${policyType?.policyType || ''}`"
      :loading="loading"
      actionButtonLabel="Save Changes"
      :handleAction="handleSave"
      :handleCancel="handleCancel"
      size="md">
      <template #content>
        <div class="flex flex-col gap-4">
          <!-- Policy Description -->
          <div class="w-full">
            <label class="label">
              <span class="label-text text-xs text-gray-500 pb-1.5">Policy Description</span>
            </label>
            <input
              type="text"
              v-model="description"
              class="input input-md w-full border border-solid"
              :class="{ 'border-red-500': error && !description.trim() }"
              placeholder="Enter policy description..." />
          </div>

          <!-- Coverage Type Selection -->
          <div v-if="!isCreatingNew" class="w-full">
            <label class="label">
              <span class="label-text text-xs text-gray-500 pb-1.5">Coverage Type</span>
            </label>
            <Combobox
              v-model="selectedCoverageType"
              :options="coverageTypeOptions"
              :placeholder="'Select coverage type'"
              input-class="input-md"
              :class="{ 'border-red-500': error && !selectedCoverageType }"
              @selected="handleCoverageTypeSelect" />
          </div>

          <!-- New Coverage Type Creation -->
          <div v-else class="w-full">
            <label class="label">
              <span class="label-text text-xs text-gray-500">New Coverage Type Name</span>
            </label>
            <input
              type="text"
              v-model="newCoverageTypeName"
              class="input input-md w-full border border-solid"
              :class="{
                'border-red-500':
                  error && (!newCoverageTypeName.trim() || props.coverageTypes.includes(newCoverageTypeName.trim())),
              }"
              placeholder="Enter new coverage type name..." />
          </div>

          <!-- Create New Toggle -->
          <div class="flex items-center gap-3">
            <label class="label form-control flex-row gap-3">
              <input
                type="checkbox"
                v-model="isCreatingNew"
                class="toggle toggle-primary border border-solid border-gray-400" />
              <span class="label-text text-sm">Create new coverage type</span>
            </label>
          </div>

          <!-- Error Display -->
          <div v-if="error" class="rounded-md border border-red-200 bg-red-50 p-3">
            <div class="flex">
              <div class="shrink-0">
                <AlertCircle class="h-5 w-5 text-red-400" />
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-red-800">Error</h3>
                <div class="mt-1 text-sm text-red-700">
                  {{ error }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </AppModal>
  </div>
</template>
